import type { Property } from '@/common/types/interaction';

export type Address = Partial<
  Pick<Property, 'street1' | 'street2' | 'city' | 'state' | 'zip'>
>;

export type FormatAddressOptions = {
  /** Include `street2` (e.g. suite/unit) in the output. */
  includeStreet2?: boolean;
  /** Drop the `+4` suffix from ZIP+4 postal codes (e.g. `94101-1234` → `94101`). */
  stripZip4?: boolean;
};

/**
 * Transform an address object into a stringified address. Pass options to
 * include `street2` or strip the `+4` suffix from ZIP+4 postal codes.
 */
export function formatAddress(
  address: Address | null | undefined,
  { includeStreet2 = false, stripZip4 = false }: FormatAddressOptions = {},
): string {
  if (!address) return '';

  const { street1, street2, city, state, zip } = address;

  const postalCode = stripZip4 && zip ? (zip.split('-')[0] ?? zip) : zip;

  const stateAndZip = [state, postalCode]
    .filter((part) => !!part)
    .join(' ')
    .trim();

  if (includeStreet2) {
    return [street1, street2, city, stateAndZip]
      .filter((part) => !!part)
      .join(', ');
  }

  return [street1, city, stateAndZip].filter((part) => !!part).join(', ');
}
